import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import API, { formatApiError } from "../lib/api";
import { openAuthFile, downloadAuthFile } from "../lib/fileOpen";
import { Button } from "../components/ui/button";
import { ArrowLeft, Plus, FilePdf, DownloadSimple, UploadSimple, PencilSimple, Trash, PaperPlaneTilt, Eye, CaretDown, CaretUp, Briefcase } from "@phosphor-icons/react";
import { toast } from "sonner";
import { ScoreRing, StatusBadge, DaysChip, StatusCounts } from "../components/compliance/ComplianceBadges";
import { AddRequirementsDialog, CertificateDialog, EditRecordDialog } from "../components/compliance/ComplianceDialogs";
import { CATEGORY_ICON, fmtD, intervalText } from "../components/compliance/complianceUtils";

export default function PropertyCompliancePage() {
  const { propertyId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const canEdit = ["super_admin", "admin", "project_manager"].includes(user?.role);
  const [d, setD] = useState(null);
  const [missing, setMissing] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [certFor, setCertFor] = useState(null);
  const [editFor, setEditFor] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [packBusy, setPackBusy] = useState(false);

  const load = useCallback(() => {
    API.get(`/compliance/properties/${propertyId}`).then((r) => setD(r.data)).catch((e) => { if (e?.response?.status === 404) setMissing(true); else toast.error("Couldn't load compliance"); });
  }, [propertyId]);
  useEffect(() => { load(); }, [load]);

  const remove = async (r) => {
    if (!window.confirm(`Remove "${r.type_name}" from this building? Its certificate history stays in the File Hub.`)) return;
    try { await API.delete(`/compliance/records/${r.id}`); toast.success("Requirement removed"); load(); } catch { toast.error("Couldn't remove"); }
  };
  const raise = async (r) => {
    try { const { data } = await API.post(`/compliance/records/${r.id}/raise-job`); toast.success(`Renewal job ${data.job.reference} raised`); load(); }
    catch (err) { toast.error(formatApiError(err?.response?.data?.detail) || "Couldn't raise job"); }
  };
  const pack = async (download) => {
    setPackBusy(true);
    try {
      const url = `/compliance/properties/${propertyId}/audit-pack.pdf`;
      if (download) await downloadAuthFile(url, `audit-pack-${(d?.property?.name || "property").replace(/[^a-z0-9]+/gi, "-")}.pdf`); else await openAuthFile(url);
    } catch { toast.error("Couldn't build the audit pack"); } finally { setPackBusy(false); }
  };

  if (missing) return <div className="rounded-lg border border-dashed p-10 text-center text-sm text-muted-foreground" data-testid="property-compliance-missing">Property not found. <Link to="/compliance" className="text-primary underline">Back to compliance</Link></div>;
  if (!d) return <div className="h-64 animate-pulse rounded-lg bg-muted/40" data-testid="property-compliance-loading" />;
  const p = d.property, roll = d.rollup;
  const cats = [...new Set(d.records.map((r) => r.category))];
  const jobFor = (r) => d.renewal_jobs.find((j) => j.compliance_record_id === r.id && !["completed", "cancelled", "declined"].includes(j.status));

  return (
    <div className="space-y-5" data-testid="property-compliance-page">
      <Link to="/compliance" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="property-compliance-back"><ArrowLeft size={14} /> All buildings</Link>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-4">
          <ScoreRing score={roll.score} size={72} testid="property-score" />
          <div>
            <h1 className="text-2xl font-bold font-[Chivo]" data-testid="property-compliance-title">{p.name}</h1>
            <p className="text-sm text-muted-foreground">{[p.address_line, p.town_city, p.postcode].filter(Boolean).join(", ")}{p.client_name && <> · <Link to={`/clients/${p.client_id}`} className="hover:underline">{p.client_name}</Link></>}</p>
            <div className="mt-2"><StatusCounts roll={roll} testid="property-status-counts" /></div>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => pack(false)} disabled={packBusy || !d.records.length} data-testid="audit-pack-open"><FilePdf size={16} className="mr-1" /> {packBusy ? "Building…" : "Audit pack"}</Button>
          <Button variant="outline" size="icon" onClick={() => pack(true)} disabled={packBusy || !d.records.length} title="Download audit pack" data-testid="audit-pack-download"><DownloadSimple size={16} /></Button>
          {canEdit && <Button onClick={() => setAddOpen(true)} data-testid="add-requirements-btn"><Plus size={16} className="mr-1" /> Add requirements</Button>}
        </div>
      </div>

      {d.records.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border p-10 text-center" data-testid="property-compliance-empty">
          <p className="font-medium">Nothing tracked for this building yet</p>
          <p className="text-sm text-muted-foreground mt-1">Add the statutory requirements that apply — gas, electrical, fire, water, lifts — and we'll keep the dates, raise renewals and build the audit pack.</p>
          {canEdit && <Button className="mt-4" onClick={() => setAddOpen(true)} data-testid="add-requirements-empty-btn"><Plus size={16} className="mr-1" /> Add requirements</Button>}
        </div>
      ) : cats.map((c) => {
        const I = CATEGORY_ICON[c] || CATEGORY_ICON.Other;
        return (
          <div key={c} className="rounded-lg border border-border bg-card overflow-hidden">
            <div className="flex items-center gap-2 px-4 py-2 bg-muted/40 border-b border-border text-xs font-semibold uppercase tracking-wider"><I size={14} /> {c}</div>
            <table className="w-full text-sm">
              <thead className="text-[11px] uppercase tracking-wider text-muted-foreground">
                <tr className="border-b border-border"><th className="text-left px-4 py-2 font-medium">Requirement</th><th className="text-left px-3 py-2 font-medium">Last inspection</th><th className="text-left px-3 py-2 font-medium">Expiry</th><th className="text-left px-3 py-2 font-medium">Status</th><th className="text-left px-3 py-2 font-medium">Provider / ref</th><th className="px-3 py-2" /></tr>
              </thead>
              <tbody>
                {d.records.filter((r) => r.category === c).map((r) => {
                  const job = jobFor(r);
                  const open = expanded[r.id];
                  return (
                    <RecordRow key={r.id} r={r} job={job} open={open} canEdit={canEdit} onToggle={() => setExpanded((e) => ({ ...e, [r.id]: !e[r.id] }))}
                      onCert={() => setCertFor(r)} onEdit={() => setEditFor(r)} onRaise={() => raise(r)} onRemove={() => remove(r)} onJob={() => navigate(`/jobs/${job.id}`)} />
                  );
                })}
              </tbody>
            </table>
          </div>
        );
      })}

      <AddRequirementsDialog open={addOpen} onOpenChange={setAddOpen} types={d.available_types} commonSet={d.common_set} propertyId={propertyId} onSaved={load} />
      <CertificateDialog open={!!certFor} onOpenChange={(o) => !o && setCertFor(null)} record={certFor} onSaved={load} />
      <EditRecordDialog open={!!editFor} onOpenChange={(o) => !o && setEditFor(null)} record={editFor} onSaved={load} />
    </div>
  );
}

function RecordRow({ r, job, open, canEdit, onToggle, onCert, onEdit, onRaise, onRemove, onJob }) {
  const viewCert = (fid) => openAuthFile(`/compliance/files/${fid}`).catch(() => toast.error("Couldn't open certificate"));
  return (
    <>
      <tr className="border-b border-border last:border-0 hover:bg-muted/20" data-testid={`compliance-record-${r.id}`}>
        <td className="px-4 py-2.5">
          <p className="font-medium">{r.type_name}</p>
          <p className="text-[11px] text-muted-foreground">Every {intervalText(r.interval_months)} · {r.lead_days}d lead{r.auto_raise_job === false ? " · manual renewals" : ""}</p>
        </td>
        <td className="px-3 py-2.5 whitespace-nowrap">{fmtD(r.last_inspection_date)}</td>
        <td className="px-3 py-2.5 whitespace-nowrap"><p>{fmtD(r.expiry_date)}</p><DaysChip r={r} testid={`record-days-${r.id}`} /></td>
        <td className="px-3 py-2.5"><StatusBadge status={r.status} testid={`record-status-${r.id}`} />{job && <button onClick={onJob} className="block mt-1 text-[11px] text-primary hover:underline inline-flex items-center gap-1" data-testid={`record-job-${r.id}`}><Briefcase size={11} /> {job.reference}</button>}</td>
        <td className="px-3 py-2.5 text-xs">
          <p>{r.provider || <span className="text-muted-foreground">—</span>}</p>
          {r.reference && <p className="font-mono text-muted-foreground">{r.reference}</p>}
          {r.certificate_file_id && <button onClick={() => viewCert(r.certificate_file_id)} className="mt-0.5 inline-flex items-center gap-1 text-primary hover:underline" data-testid={`record-cert-${r.id}`}><Eye size={12} /> Certificate</button>}
        </td>
        <td className="px-3 py-2.5 text-right whitespace-nowrap">
          {canEdit && <Button size="sm" variant="outline" className="h-7 mr-1" onClick={onCert} data-testid={`record-upload-${r.id}`}><UploadSimple size={13} className="mr-1" /> Record inspection</Button>}
          {canEdit && !job && r.status !== "valid" && <Button size="icon" variant="ghost" className="h-7 w-7" title="Raise renewal job now" onClick={onRaise} data-testid={`record-raise-${r.id}`}><PaperPlaneTilt size={14} /></Button>}
          {canEdit && <Button size="icon" variant="ghost" className="h-7 w-7" onClick={onEdit} data-testid={`record-edit-${r.id}`}><PencilSimple size={14} /></Button>}
          {canEdit && <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={onRemove} data-testid={`record-remove-${r.id}`}><Trash size={14} /></Button>}
          {(r.history?.length > 0 || r.notes) && <Button size="icon" variant="ghost" className="h-7 w-7" onClick={onToggle} data-testid={`record-history-${r.id}`}>{open ? <CaretUp size={14} /> : <CaretDown size={14} />}</Button>}
        </td>
      </tr>
      {open && (
        <tr className="bg-muted/10 border-b border-border" data-testid={`record-history-panel-${r.id}`}>
          <td colSpan={6} className="px-4 py-3 text-xs">
            {r.notes && <p className="mb-2 whitespace-pre-wrap"><span className="font-semibold">Notes:</span> {r.notes}</p>}
            {r.history?.length > 0 && (
              <div>
                <p className="font-semibold mb-1">Previous certificates</p>
                <ul className="space-y-1">
                  {[...r.history].reverse().map((h, i) => (
                    <li key={i} className="flex flex-wrap items-center gap-2 text-muted-foreground">
                      <span>{fmtD(h.last_inspection_date)} → {fmtD(h.expiry_date)}</span>{h.provider && <span>· {h.provider}</span>}{h.reference && <span className="font-mono">· {h.reference}</span>}
                      {h.certificate_file_id && <button onClick={() => viewCert(h.certificate_file_id)} className="text-primary hover:underline inline-flex items-center gap-1"><Eye size={11} /> {h.certificate_file_name || "file"}</button>}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}
